import { useEffect, useState } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { GestorDashboardClientView } from '@/components/Gestores/GestorDashboardClientView'
import { GestorDashboardCollaboratorView } from '@/components/Gestores/GestorDashboardCollaboratorView'
import { GestorDashboardFilters } from '@/components/Gestores/GestorDashboardFilters'
import { GestorDashboardPeriodControls } from '@/components/Gestores/GestorDashboardPeriodControls'
import { GestorDashboardFinancialView } from '@/components/Gestores/GestorDashboardFinancialView'
import { GestorDashboardOverviewView } from '@/components/Gestores/GestorDashboardOverviewView'
import { GestorDashboardSaveFilterModal } from '@/components/Gestores/GestorDashboardSaveFilterModal'
import { GestorDashboardSubTabs } from '@/components/Gestores/GestorDashboardSubTabs'
import { formatDashboardDateTime } from '@/components/Gestores/gestorDashboardFormat'
import { formatDashboardRangeLabel } from '@/components/Gestores/gestorDashboardDateRangeUtils'
import type { GestorDashboardSubTab } from '@/components/Gestores/gestorDashboardTypes'
import { useGestorDashboardTab } from '@/hooks/useGestorDashboardTab'

type Props = {
  reloadToken?: number
}

export function GestorDashboardTab({ reloadToken = 0 }: Props) {
  const dashboard = useGestorDashboardTab(true)
  const [subTab, setSubTab] = useState<GestorDashboardSubTab>('overview')

  useEffect(() => {
    if (reloadToken > 0) void dashboard.reload()
  }, [reloadToken, dashboard.reload])

  useEffect(() => {
    if (subTab === 'financial') {
      void dashboard.loadFinancial(false)
    }
  }, [subTab, dashboard.loadFinancial, dashboard.filters.createdFrom, dashboard.filters.createdTo])

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
        <div>
          <h2 className='text-base font-semibold text-text-primary'>Dashboard Marketing</h2>
          <p className='text-sm text-text-muted'>Dados operacionais e financeiros integrados ao eKyte.</p>
        </div>
        <div className='flex flex-wrap items-center gap-2 lg:justify-end'>
          <GestorDashboardPeriodControls
            createdFrom={dashboard.filters.createdFrom}
            createdTo={dashboard.filters.createdTo}
            disabled={dashboard.loading}
            onApply={dashboard.setPeriod}
          />
          <button
            type='button'
            disabled={dashboard.loading}
            onClick={() => void dashboard.reload()}
            className='inline-flex h-10 items-center gap-2 rounded-full border border-[var(--glass-border)] px-4 text-sm font-medium text-text-secondary transition-colors hover:bg-white/[0.04] hover:text-text-primary disabled:opacity-50'
          >
            <RefreshCw size={16} className={dashboard.loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>

      <GestorDashboardSubTabs value={subTab} onChange={setSubTab} />

      {dashboard.data?.syncedAt && (
        <p className='text-xs text-text-muted'>
          Período: {formatDashboardRangeLabel(dashboard.data.period.createdFrom, dashboard.data.period.createdTo, true)}
          {' · '}
          {dashboard.data.summary.entryCount} apontamentos
          {' · '}
          {dashboard.filterOptions.executors.length} executores no período
          {' · '}
          Última sincronização: {formatDashboardDateTime(dashboard.data.syncedAt)}
          {dashboard.data.meta?.source === 'database'
            ? ' · dados salvos no banco'
            : dashboard.data.meta?.source === 'memory'
              ? ' · cache em memória'
              : dashboard.data.meta?.fromCache
                ? ' · cache ativo'
                : ''}
          {dashboard.data.meta?.periodDays ? ` · ${dashboard.data.meta.periodDays} dias` : ''}
        </p>
      )}

      <div className='min-w-0'>
        <GestorDashboardFilters
          workspaces={dashboard.filters.workspaces}
          executors={dashboard.filters.executors}
          taskType={dashboard.filters.taskType}
          search={dashboard.filters.search}
          loading={dashboard.loading || !dashboard.data}
          executorOptions={dashboard.filterOptions.executors}
          workspaceOptions={dashboard.filterOptions.workspaces}
          taskTypeOptions={dashboard.filterOptions.taskTypes}
          presets={dashboard.presets}
          activePresetId={dashboard.activePresetId}
          presetsLoading={dashboard.presetsLoading}
          onChange={(key, value) => dashboard.setFilter(key, value as never)}
          onClearAll={dashboard.clearFilters}
          onApplyPreset={dashboard.applyPreset}
          onCreatePreset={() => dashboard.openSaveFilterModal(null)}
          onEditPreset={(preset) => dashboard.openSaveFilterModal(preset)}
        />
      </div>

      <GestorDashboardSaveFilterModal
        open={dashboard.saveModalOpen}
        mode={dashboard.editingPreset ? 'edit' : 'create'}
        initialName={dashboard.editingPreset?.name}
        initialCriteria={dashboard.editingPreset?.criteria}
        currentCriteria={dashboard.currentCriteria}
        catalogs={dashboard.catalogs}
        workspaceOptions={dashboard.filterOptions.workspaces}
        executorOptions={dashboard.filterOptions.executors}
        preset={dashboard.editingPreset}
        loading={dashboard.loading}
        onClose={dashboard.closeSaveFilterModal}
        onSave={(input) => void dashboard.saveFilterPreset(input)}
        onDelete={dashboard.editingPreset ? () => void dashboard.deleteFilterPreset() : undefined}
      />

      {dashboard.error && (
        <div
          role='alert'
          className='flex items-start gap-2 rounded-xl border border-warning/20 bg-warning/10 p-3 text-sm text-warning'
        >
          <AlertTriangle size={16} className='mt-0.5 shrink-0' aria-hidden />
          <span>{dashboard.error}</span>
        </div>
      )}

      {dashboard.data?.warnings?.length ? (
        <div className='rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 text-xs text-text-muted'>
          {dashboard.data.warnings.join(' ')}
        </div>
      ) : null}

      {dashboard.data && subTab === 'overview' && (
        <GestorDashboardOverviewView overview={dashboard.data.overview} summary={dashboard.data.summary} />
      )}

      {dashboard.data && subTab === 'collaborator' && (
        <GestorDashboardCollaboratorView
          collaborator={dashboard.data.collaborator}
          summary={dashboard.data.summary}
          entries={dashboard.data.entries}
          selectedExecutor={dashboard.filters.executors.length === 1 ? dashboard.filters.executors[0] ?? '' : ''}
          period={dashboard.data.period}
          squadId={dashboard.filters.squadId}
          loading={dashboard.loading}
          onExecutorSelect={dashboard.selectExecutorScope}
          onExecutorClear={dashboard.clearExecutorScope}
        />
      )}

      {dashboard.data && subTab === 'clients' && (
        <GestorDashboardClientView
          clients={dashboard.data.clients}
          summary={dashboard.data.summary}
          entries={dashboard.data.entries}
          selectedWorkspace={dashboard.filters.workspaces.length === 1 ? dashboard.filters.workspaces[0] ?? '' : ''}
          selectedPhase={dashboard.filters.phase}
          period={dashboard.data.period}
          squadId={dashboard.filters.squadId}
          loading={dashboard.loading}
          onWorkspaceSelect={(workspace) => dashboard.selectClientScope('workspace', workspace)}
          onPhaseSelect={(phase) => dashboard.selectClientScope('phase', phase)}
          onScopeClear={dashboard.clearClientScope}
        />
      )}

      {dashboard.data && subTab === 'financial' && (
        <GestorDashboardFinancialView financial={dashboard.data.financial} />
      )}
    </div>
  )
}
